// Janus C1 (2026-09-24): port of compute_senses() (includes/functions/janus/core/compute/senses.php) for the possessor compile — per
// sense: host + default-provider carriers + the shared dynamic walker (ft_compute_collect_dynamic_integer_sources: status / passive /
// inventory incl. an equipped row's nested abilities / feats, own layer only) → the inventory non-stacking cap → total = the row sum →
// ft_compute_persist('senses', <name>, total, sources). Only the changed senses path is copied.
// The first held creature form replaces the authored base; dynamic modifiers still belong to the host.
import type { ItemFieldRow } from './item-field-reader';
import { readLayerScoringToken, readNodeItemId } from './layer-cell-readers';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { createStatusLayerReadContext } from './status-row-construction';
import { createFormulaReadContext } from './formula-read-context';
import { readCreaturePayloadStatuses } from './creature-status-reader';
import { createDynamicIntegerSourceCollector } from './dynamic-integer-sources';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import { capInventorySourceRows, createComputedPersistence } from './computed-persistence';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { pristineFragments, sourceRow } from './creature-attacks';
import type { CreatureAttacksDependencies } from './creature-attacks';
import { integerCompileWorkingRow, removeIntegerCompilePath } from './integer-compute-slots';
import { resolveCreatureShapeshiftForm } from './creature-shapeshift-form';

type Row = Record<string, unknown>;
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key)
    ? (value as Row)[key] : undefined;
const scalar = (value: unknown): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, '0') ? own(value, '0') : value;
const stringValue = (value: unknown): string => typeof scalar(value) === 'string' ? String(scalar(value)) : '';
const intValue = (value: unknown): number => { const n = Number(scalar(value) ?? 0); return Number.isFinite(n) ? Math.trunc(n) : 0; };
/** FT_COMPUTE_SENSES_NAMES, in PHP's order (the persisted key order). */
export const FT_SENSE_NAMES = ['nightsight', 'fiendsight', 'blindsight', 'ghostsight', 'spellsight', 'truesight', 'tremorsense', 'telepathy', 'windsense'] as const;

export type CreatureSensesCompileResult = { ok: true; senses: Row | undefined } | { ok: false; reason: string };

export function compileCreatureSenses(input: ItemFieldRow, deps: CreatureAttacksDependencies, form: ItemFieldRow | null = null,
    formAlreadyResolved = false): CreatureSensesCompileResult {
    const host = integerCompileWorkingRow(input);
    const readers = createStatusLayerReadContext(deps);
    const formulaReaders = createFormulaReadContext(deps);
    const providers = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId: readNodeItemId, resolveCarrierRef: deps.resolveCarrierRef });
    const persist = createComputedPersistence(deps, createComputeSourceCleaner(deps));
    const collector = createDynamicIntegerSourceCollector(deps);
    if (form === null && !formAlreadyResolved) {
        const selected = resolveCreatureShapeshiftForm(host, deps);
        if (!selected.ok) return { ok: false, reason: `unported:senses:${selected.reason}` };
        form = selected.form;
    } else {
        // A caller-supplied finalized form already owns its template overlay.
        for (const status of Object.values(readCreaturePayloadStatuses(host, readers.layerValue, deps.resolveCarrierRef)).filter(arrayLike)) {
            if (!arrayLike(own(status, 'status_arr'))) return { ok: false, reason: 'unported:senses:status-undefined' };
            if (formulaReaders.itemField(status, 'item_type') == null) return { ok: false, reason: 'unported:senses:status-type-undefined' };
        }
    }
    const base = form ?? host;
    removeIntegerCompilePath(host, ['computed', 'creature', 'senses']);
    let out: ItemFieldRow = host;
    for (const sense of FT_SENSE_NAMES) {
        const score = (fragments: unknown[]): number => scoreCarrierIntegerLayer({ item_layer_types: { [sense]: fragments } } as ItemFieldRow, sense, 0, false, deps.metadata, formulaReaders);
        for (const row of [base, ...providers.providers(base)]) for (const fragment of pristineFragments(row, sense)) {
            if ((readLayerScoringToken(fragment) ?? '').includes('[')) return { ok: false, reason: 'unported:senses:token-reference' };
        }
        let sources: Row[] = [];
        const direct = pristineFragments(base, sense);
        if (direct.length) {
            const value = score(direct);
            const hostName = formulaReaders.itemField(base, 'item_name');
            if (value !== 0) sources.push(sourceRow('host', hostName == null ? 'Host' : stringValue(hostName), value, stringValue(formulaReaders.itemField(base, 'item_id')), 'host'));
        }
        for (const carrier of providers.providers(base)) {
            const fragments = pristineFragments(carrier, sense);
            if (!fragments.length) continue;
            const value = score(fragments);
            if (value === 0) continue;
            const items = own(carrier, 'items'), type = stringValue(own(items, 'item_type')) || 'carrier';
            sources.push(sourceRow(type, stringValue(own(items, 'item_name')), value, stringValue(own(items, 'item_id')), type));
        }
        let dynamicRows: Row[];
        try { [dynamicRows] = collector.dynamic(host, sense, true, true, form); }
        catch (error) { return { ok: false, reason: 'unported:senses:walker:' + (error instanceof Error ? error.message : String(error)).slice(0, 60) }; }
        sources.push(...dynamicRows);
        [sources] = capInventorySourceRows(sources as unknown as ItemFieldRow) as unknown as [Row[], number];
        sources = Object.values(sources);
        const total = sources.reduce((sum, row) => sum + intValue(row.value ?? 0), 0);
        out = persist(out, 'senses', sense, total, { base: 0, sources, cap: null, locked: false });
    }
    return { ok: true, senses: own(own(own(out, 'computed'), 'creature'), 'senses') as Row | undefined };
}
